import { FC, useEffect, useRef } from 'react';
import {
  Flex,
  SystemStyleObject,
  Table,
  TableContainer,
  TableContainerProps,
  Tbody,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { getColorValue } from '@shinetools/sunshine';
import { useMotionValueEvent, useScroll } from 'framer-motion';

import { PaymentCardSortBy, SortOrder } from '__generated__/GQL';
import SunshineIcon, { IconName } from 'components/_core/SunshineIcon';
import Text from 'components/_core/Text';
import Loader from 'components/Loader';
import { PaymentCardListTable_ItemFragment } from 'features/PaymentCards/pages/PaymentCardList/hooks/usePaymentCardsList/paymentCardsList.gql';

import { LAYOUT_BODY_CLASSNAME } from '../../constants';
import type { PaymentCardListObservableQuery } from '../../hooks/usePaymentCardsList';
import { usePaymentCardsList } from '../../hooks/usePaymentCardsList';
import { usePaymentCardsListState } from '../PaymentCardsListFiltersProvider';
import { PaymentCardListTableRow } from './components/PaymentCardListTableRow';
import locales from './locales';

interface Props extends TableContainerProps {
  isFetchingMore: boolean;
  items: PaymentCardListTable_ItemFragment[];
  onBottomReached?: () => void;
  listObservable: PaymentCardListObservableQuery;
  selectedItemId: string | null;
}

const tableStyles = {
  '& tbody': {
    '& tr': {
      '& td': {
        '&:first-of-type': {
          pl: 'space-16',
        },
        '&:last-of-type': {
          pr: 'space-16',
        },
        pr: 'space-16',
        py: 'space-16',
      },
      '&:last-child td:first-child': {
        borderBottomLeftRadius: 'radius-4',
      },
      '&:last-child td:last-child': {
        borderBottomLeftRadius: 'radius-4',
      },
      '&:last-of-type': {
        borderBottom: 'none',
      },
      borderBottom: '1px solid',
      borderColor: 'inherit',
    },
    boxShadow: `0 0 0 1px ${getColorValue('grey.300')}`,
  },
  '& thead th': {
    '&:first-of-type': {
      pl: 'space-16',
    },
    '&:last-of-type': {
      pr: 'space-16',
    },
    pr: 'space-16',
    py: 'space-8',
  },
  tableLayout: 'fixed',
} satisfies SystemStyleObject;

const ThText: typeof Text = (props) => (
  <Text
    fontWeight="weight-500"
    size="sm"
    textAlign="left"
    variant="light"
    {...props}
  />
);

export const PaymentCardListTable: FC<Props> = ({
  isFetchingMore,
  items,
  listObservable,
  onBottomReached,
  selectedItemId,
  ...props
}) => {
  const { dispatch, ordering } = usePaymentCardsListState();

  const { paymentCardsListIsLoading } = usePaymentCardsList();

  const columnStyle = {
    alignItems: 'center',
    cursor: paymentCardsListIsLoading ? undefined : 'pointer',
    display: 'flex',
    width: '100%',
  } satisfies SystemStyleObject;

  const $layoutRef = useRef<HTMLDivElement | null>(null);
  const $container = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    $layoutRef.current = document.querySelector(`.${LAYOUT_BODY_CLASSNAME}`);
  }, []);

  const { scrollYProgress } = useScroll({
    axis: 'y',
    container: $layoutRef,
    layoutEffect: false,
    target: $container,
  });

  useMotionValueEvent(scrollYProgress, 'change', (latest) => {
    if (latest === 1 && onBottomReached) {
      onBottomReached();
    }
  });

  const getColor = (column: PaymentCardSortBy) => {
    if (ordering.currentOrdering.sortBy === column) {
      return 'grey.800';
    }
    return 'grey.600';
  };

  const getOrderBy = (
    column: Exclude<PaymentCardSortBy, PaymentCardSortBy.CreatedAt>,
  ): SortOrder => {
    if (ordering.currentOrdering.sortBy === column) {
      return ordering.currentOrdering.orderBy === SortOrder.Asc
        ? SortOrder.Desc
        : SortOrder.Asc;
    }
    return ordering[column];
  };

  const getSunshineIconName = (column: PaymentCardSortBy): IconName => {
    return ordering[column as keyof typeof ordering] === SortOrder.Asc
      ? 'chevron-up'
      : 'chevron-down';
  };

  const dispatchAction = (
    column: Exclude<PaymentCardSortBy, PaymentCardSortBy.CreatedAt>,
  ) => {
    if (paymentCardsListIsLoading) {
      return;
    }
    dispatch({
      payload: {
        orderBy: getOrderBy(column),
        sortBy: column,
      },
      type: 'UPDATE_SORT_ORDER_BY',
    });
  };

  return (
    <VStack alignItems="stretch" gap="space-8">
      <TableContainer
        borderColor="grey.300"
        overflowX="unset"
        overflowY="unset"
        ref={$container}
        {...props}
      >
        <Table
          borderColor="inherit"
          sx={tableStyles}
          variant="unstyled"
          width="100%"
        >
          <Thead>
            <Tr>
              <Th width="35%">
                <Flex gap="space-8">
                  <ThText color="grey.600">
                    {locales.table.columns.name.title}
                  </ThText>
                </Flex>
              </Th>
              <Th width="25%">
                <Flex
                  gap="space-8"
                  onClick={() => dispatchAction(PaymentCardSortBy.OwnerName)}
                  style={columnStyle}
                >
                  <ThText color={getColor(PaymentCardSortBy.OwnerName)}>
                    {locales.table.columns.owner.title}
                  </ThText>
                  <SunshineIcon
                    color={getColor(PaymentCardSortBy.OwnerName)}
                    name={getSunshineIconName(PaymentCardSortBy.OwnerName)}
                  />
                </Flex>
              </Th>
              <Th width="20%">
                <Flex
                  gap="space-8"
                  onClick={() => dispatchAction(PaymentCardSortBy.LimitPayment)}
                  style={columnStyle}
                >
                  <ThText color={getColor(PaymentCardSortBy.LimitPayment)}>
                    {locales.table.columns.paymentLimit.title}
                  </ThText>
                  <SunshineIcon
                    color={getColor(PaymentCardSortBy.LimitPayment)}
                    name={getSunshineIconName(PaymentCardSortBy.LimitPayment)}
                  />
                </Flex>
              </Th>
              <Th width="20%">
                <Flex
                  gap="space-8"
                  justifyContent="flex-end"
                  onClick={() => dispatchAction(PaymentCardSortBy.Status)}
                  style={columnStyle}
                >
                  <ThText color={getColor(PaymentCardSortBy.Status)}>
                    {locales.table.columns.status.title}
                  </ThText>
                  <SunshineIcon
                    color={getColor(PaymentCardSortBy.Status)}
                    name={getSunshineIconName(PaymentCardSortBy.Status)}
                  />
                </Flex>
              </Th>
            </Tr>
          </Thead>
          <Tbody borderRadius="radius-4">
            {items.map((item) => (
              <PaymentCardListTableRow
                isSelected={item.creditCardId === selectedItemId}
                item={item}
                key={item.creditCardId}
                listObservable={listObservable}
              />
            ))}
          </Tbody>
        </Table>
      </TableContainer>
      {isFetchingMore ? <Loader /> : null}
    </VStack>
  );
};
